/**
 * BreakdownChartWidget — a metric broken down by a dimension (department /
 * site / status / …) as a bar chart, with click-to-drill: clicking a bar opens
 * the records grid filtered to that value (department-/site-/user-wise
 * performance + chart drill-through in one).
 *
 * Config: { metric, groupBy, chartType='hbar', title, limit, showValues }.
 *
 * `showValues` starts the chart with each value printed on it (for radial types
 * the figure rides in the legend, since a label outside a ring clips in a small
 * card). It is a starting position only — the chart toolbar hands the choice to
 * the reader.
 */
import React, { useState } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { isSiteRequiredError, SiteRequiredNotice } from '../utils/siteScope';
import { CARD, HEADING } from '../ui';
import { fetchBreakdown } from '../api/client';
import ChartWidget from './ChartWidget';
import DrilldownModal from './DrilldownModal';

export default function BreakdownChartWidget({
  title, metric, groupBy, stackBy = null, chartType = 'hbar', limit = 12,
  showValues = false, height,
}) {
  const { module, siteId, time, departmentId, dimensions, refreshMs } = useDashboard();
  const timeKey = time ? JSON.stringify(time) : '';
  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';
  // A stacked pivot reads better as vertical stacked bars.
  const effectiveType = stackBy ? (chartType === 'hbar' ? 'stacked-bar' : chartType) : chartType;

  const { data, loading, error } = useDashboardData(
    (signal) => {
      const body = { module, metric, group_by: groupBy, limit };
      if (stackBy) body.stack_by = stackBy;
      if (siteId) body.site_id = siteId;
      if (departmentId) body.department_id = departmentId;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      if (time) body.time = time;
      return fetchBreakdown(body, { signal });
    },
    [module, metric, groupBy, stackBy, siteId, timeKey, departmentId, dimsKey, limit],
    { enabled: !!module && !!metric && !!groupBy, refreshMs },
  );

  const [drill, setDrill] = useState(null);

  // Click a bar → drill into that group's records. Dimension group-bys filter
  // by that dimension value; 'site' scopes by site_id. An empty group value
  // ("Unspecified") still drills — just without the extra dimension filter.
  const onPointClick = (d) => {
    const key = d?.key ?? d?.payload?.key;
    const label = d?.label ?? d?.payload?.label;
    if (key === undefined || key === null) return;
    setDrill({ key, label });
  };

  if (isSiteRequiredError(error)) {
    return (
      <div className={`${CARD} p-4`}>
        {title && <h3 className={`mb-2 ${HEADING}`}>{title}</h3>}
        <SiteRequiredNotice />
      </div>
    );
  }

  return (
    <>
      <ChartWidget
        type={effectiveType}
        data={data?.data || []}
        xKey="label"
        series={data?.series || [{ key: 'value', label: 'Count' }]}
        title={title}
        height={height}
        showValues={showValues}
        loading={loading}
        error={error}
        onPointClick={onPointClick}
      />
      <DrilldownModal
        open={!!drill}
        metric={metric}
        title={`${title}: ${drill?.label ?? ''}`}
        extraDimensions={drill && drill.key && groupBy !== 'site' ? { [groupBy]: [drill.key] } : null}
        siteOverride={drill && drill.key && groupBy === 'site' ? drill.key : null}
        onClose={() => setDrill(null)}
      />
    </>
  );
}
